import React from "react";
import { Link } from "react-router-dom";
import { Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";

import CustomPagination from "../../../../../core/common/customPagination/CustomPagination";
import { all_routes } from "../../../../router/all_routes";
import { toMinutes } from "../helpers/attendanceAdmin.helper";

import type { TableDataItem } from "../types";


interface AttendanceAdminTableProps {
  rows: TableDataItem[];
  loading: boolean;
  page: number;
  limit: number;
  total: number;
  companyId: string | number | null;
  companyType: string | null;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onViewLogs: (record: TableDataItem) => void;
}

const formatStatusBadge = (record: TableDataItem) => {
  const renderTag = (
    label: string,
    type: "Present" | "Late" | "Absent" | "Week Off" | "Holiday",
  ) => {
    if (type === "Late") {
      return (
        <span
          className="badge badge-primary d-inline-flex align-items-center justify-content-center"
          style={{
            width: "auto",
          }}
        >
          <i className="ti ti-point-filled me-1" />
          {label}
        </span>
      );
    }
    const cls =
      type === "Present"
        ? "badge-success-transparent"
        : type === "Week Off"
          ? "badge-info-transparent"
          : type === "Holiday"
            ? "badge-warning-transparent"
            : "badge-danger-transparent";
    return (
      <span
        className={`badge d-inline-flex align-items-center justify-content-center ${cls}`}
        style={{ width: "auto" }}
      >
        <i className="ti ti-point-filled me-1" />
        {label}
      </span>
    );
  };

  return (
    <div className="d-flex flex-column gap-1">
      {record.Status === "Present" && record.Late !== "0m" && renderTag("Present (Late)", "Late")}
      {record.Status === "Present" && record.Late === "0m" && renderTag("Present", "Present")}
      {record.Status === "Absent" && renderTag("Absent", "Absent")}
      {record.Status === "Week Off" && renderTag("Week Off", "Week Off")}
      {record.Status === "Holiday" && renderTag("Holiday", "Holiday")}
    </div>
  );
};

const renderProductionHours = (record: TableDataItem) => {
  const mins = toMinutes(record.ProductionHours);
  let color: "default" | "success" | "warning" | "error" | "processing" = "default";
  let displayText: string = record.ProductionHours;

  if (record.CheckOut === "N/A" && mins > 8 * 60) {
    color = "warning";
    displayText = "(No Checkout)";
  } else if (mins < 8 * 60) {
    color = "error";
  } else if (mins <= 9 * 60) {
    color = "success";
  } else {
    color = "processing";
  }

  return (
    <Tag color={color} style={{ fontSize: 11.5, margin: 0, display: "inline-flex", alignItems: "center" }}>
      <i className="ti ti-clock-hour-11 me-1" />
      {displayText}
    </Tag>
  );
};

const AttendanceAdminTable: React.FC<AttendanceAdminTableProps> = ({
  rows,
  loading,
  page,
  limit,
  total,
  companyId,
  companyType,
  onPageChange,
  onLimitChange,
  onViewLogs,
}) => {
  const totalPages = total === 0 ? 1 : Math.max(1, Math.ceil(total / limit));
  const start = total === 0 ? 0 : (page - 1) * limit;
  const paged = rows.slice(start, start + limit);

  const columns: ColumnsType<TableDataItem> = [
    {
      title: "Sr No.",
      key: "srNo",
      width: 70,
      align: "center",
      fixed: "left",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
          {(page - 1) * limit + index + 1}
        </span>
      ),
    },
    {
      title: "Employee",
      dataIndex: "Employee",
      key: "Employee",
      fixed: "left",
      width: 210,
      render: (text: string, record) => {
        const name = record.Employee || "";
        const image = record.Image || "";
        return (
          <Link className="d-flex align-items-center"
            to={`${all_routes.employeeAttendanceLogsForAdmin}?userid=${record.user_id}&company_type=${companyType ?? ""}&company_id=${companyId ?? ""}`}>
            {image ? (
              <img
                src={image}
                alt={name}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  objectFit: "cover",
                  marginRight: 10,
                }}
              />
            ) : (
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#1d71e3",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: 13,
                }}
              >
                {name.charAt(0)}
              </div>
            )}
            <span style={{ fontSize: 13.5, fontWeight: 500, color: "#0f172a" }}>
              {name}
            </span>
          </Link>
        );
      },
      sorter: (a, b) => a.Employee.localeCompare(b.Employee),
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: 130,
      align: "center",
      render: (_: string, record) => formatStatusBadge(record),
      sorter: (a, b) => a.Status.localeCompare(b.Status),
    },
    {
      title: "Check In",
      dataIndex: "CheckIn",
      key: "CheckIn",
      width: 120,
      align: "center",
      render: (text: string) => (
        <span style={{ fontSize: 13 }}>{text || "—"}</span>
      ),
      sorter: (a, b) => (a.CheckIn || "").localeCompare(b.CheckIn || ""),
    },
    {
      title: "Check Out",
      dataIndex: "CheckOut",
      key: "CheckOut",
      width: 120,
      align: "center",
      render: (text: string) => (
        <span style={{ fontSize: 13 }}>{text || "—"}</span>
      ),
      sorter: (a, b) => (a.CheckOut || "").localeCompare(b.CheckOut || ""),
    },
    {
      title: "Break",
      dataIndex: "Break",
      key: "Break",
      width: 120,
      align: "center",
      render: (text: string) => (
        <span style={{ fontSize: 13 }}>{text || "—"}</span>
      ),
    },
    {
      title: "Late",
      dataIndex: "Late",
      key: "Late",
      width: 100,
      align: "center",
      render: (text: string) => {
        if (!text || text === "N/A" || text === "0m") {
          return <span style={{ fontSize: 13, color: "#94a3b8" }}>—</span>;
        }
        return (
          <Tag color="orange" style={{ fontSize: 11.5, margin: 0 }}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Production Hours",
      dataIndex: "ProductionHours",
      key: "ProductionHours",
      width: 160,
      align: "center",
      render: (_text: string, record) => renderProductionHours(record),
      sorter: (a, b) => toMinutes(a.ProductionHours) - toMinutes(b.ProductionHours),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <div className="d-inline-flex align-items-center gap-1">
          <Tooltip title="View Logs">
            <button
              type="button"
              className="btn btn-icon btn-sm btn-light"
              style={{ borderRadius: 6 }}
              onClick={() => onViewLogs(record)}
            >
              <i className="ti ti-clipboard-list" />
            </button>
          </Tooltip>
          <Tooltip title="View Attendance">
            <Link
              to={`${all_routes.employeeAttendanceLogsForAdmin}?userid=${record.user_id}&company_type=${companyType ?? ""}&company_id=${companyId ?? ""}`}
              className="btn btn-icon btn-sm btn-light"
              style={{ borderRadius: 6 }}
            >
              <i className="ti ti-eye" />
            </Link>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="card-body p-0 mb-3 border-0">
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12, border: "1px solid #e2e8f0" }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            />
            <p className="mt-3 text-muted">Loading attendance...</p>
          </div>
        </div>
      ) : total > 0 ? (
        <div
          className="bg-white"
          style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}
        >
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={paged}
            rowKey="key"
            size="middle"
            pagination={false}
            scroll={{ x: 1300 }}
            rowClassName={(_, index) =>
              index % 2 === 0
                ? "data-table-row-light"
                : "data-table-row-dark"
            }
          />
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            hasNextPage={page * limit < total}
            hasPrevPage={page > 1}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12, border: "1px solid #e2e8f0" }}
        >
          <div className="text-center">
            <i className="ti ti-calendar-off" style={{ fontSize: 48, color: "#cbd5e1" }} />
            <h5 className="mt-3 mb-2 fw-semibold">No attendance records found</h5>
            <p className="text-muted mb-0">Try adjusting your filters.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceAdminTable;
